import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import {
  faCalendar,
  faSearch,
  faTimesCircle,
  faFilter,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  FilterInputTypes,
  IFilterConfiguration,
} from 'src/app/core/models/IFilterConfiguration';

@Component({
  selector: 'app-filter-v2',
  templateUrl: './filter-v2.component.html',
  styleUrls: ['./filter-v2.component.css'],
})
export class FilterV2Component implements OnInit {
  @Input() allowedFilterTypes: IFilterConfiguration[];
  @Input() orderByTypes: IFilterConfiguration[];
  /** Indica carga de datos del grid tras aplicar filtros */
  @Input() isLoading = false;

  @Output() removeFilters = new EventEmitter();

  params: Params = {};

  filtersByGroup: Record<string, IFilterConfiguration[]> = {};
  requirements: Record<string, string> = {};

  /** Valores de campos texto/número/select/checkbox */
  fieldValues: Record<string, string | number | boolean> = {};
  /** Valores para ngbDatepicker */
  dateValues: Record<string, NgbDateStruct | null> = {};

  orderByField = '';
  orderByDescending = false;

  iconCalendar = faCalendar;
  iconSearch = faSearch;
  iconX = faTimesCircle;
  iconFilter = faFilter;
  iconSpinner = faSpinner;
  filterInputTypes = FilterInputTypes;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    if (!this.allowedFilterTypes) {
      this.allowedFilterTypes = [];
    }

    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = params;
      this.applyQueryParamsToForm(params);
    });

    this.allowedFilterTypes.forEach((filter) => {
      const group = filter.group || 'Filtros';
      if (!this.filtersByGroup[group]) {
        this.filtersByGroup[group] = [];
      }
      this.filtersByGroup[group].push(filter);

      const req = filter.requirement;
      if (req !== undefined && this.requirements[req] === undefined) {
        this.requirements[req] = '';
      }
    });
  }

  private applyQueryParamsToForm(params: Params): void {
    this.allowedFilterTypes.forEach((f) => {
      if (f.type === FilterInputTypes.DATE) {
        this.dateValues[f.name] = this.parseDateParam(params[f.name]);
      } else if (f.type === FilterInputTypes.BOOLEAN) {
        const p = params[f.name];
        if (p === true || p === 'true') {
          this.fieldValues[f.name] = true;
        } else if (p === false || p === 'false') {
          this.fieldValues[f.name] = false;
        } else {
          this.fieldValues[f.name] = '';
        }
      } else if (f.type === FilterInputTypes.NUMBER) {
        const p = params[f.name];
        this.fieldValues[f.name] =
          p !== undefined && p !== null && String(p) !== ''
            ? Number(p)
            : '';
      } else if (f.type === FilterInputTypes.CHECKBOX) {
        const p = params[f.name];
        this.fieldValues[f.name] = p === true || p === 'true' || p === 1;
      } else {
        const p = params[f.name];
        this.fieldValues[f.name] =
          p !== undefined && p !== null ? String(p) : '';
      }
    });

    if (params['OrderBy']) {
      this.orderByField = String(params['OrderBy']);
      this.orderByDescending = false;
    } else if (params['OrderByDescending']) {
      this.orderByField = String(params['OrderByDescending']);
      this.orderByDescending = true;
    } else {
      this.orderByField = '';
      this.orderByDescending = false;
    }
  }

  private parseDateParam(raw: string | number | boolean | undefined): NgbDateStruct | null {
    if (raw === undefined || raw === null || raw === '') {
      return null;
    }
    const s = String(raw).trim();
    const iso = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
    if (iso) {
      return { year: +iso[1], month: +iso[2], day: +iso[3] };
    }
    const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
    if (dmy) {
      return { year: +dmy[3], month: +dmy[2], day: +dmy[1] };
    }
    return null;
  }

  ddOnChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const name = target.name;
    const val = target.value;

    Object.keys(this.requirements).forEach((key) => {
      const keyArr = key.split('.');
      if (this.requirements[keyArr[0]] == '') {
        this.requirements[key] = '';
      }
      if (
        keyArr[keyArr.length - 2] == name ||
        keyArr[keyArr.length - 1] == name
      ) {
        if (
          !isNaN(parseInt(keyArr[keyArr.length - 1], 10)) &&
          val !== keyArr[keyArr.length - 1]
        ) {
          this.requirements[key] = '';
        } else {
          this.requirements[key] = val;
        }
      }
    });
  }

  saveFilters(): void {
    const qp: Record<string, string | number | boolean | null> = {};

    this.allowedFilterTypes.forEach((f) => {
      qp[f.name] = null;
    });
    qp['OrderBy'] = null;
    qp['OrderByDescending'] = null;

    this.allowedFilterTypes.forEach((f) => {
      if (
        f.requirement !== undefined &&
        this.requirements[f.requirement] === ''
      ) {
        return;
      }

      if (f.type === FilterInputTypes.DATE) {
        const d = this.dateValues[f.name];
        if (d?.year) {
          qp[f.name] = `${d.year}/${String(d.month).padStart(2, '0')}/${String(d.day).padStart(2, '0')}`;
        }
      } else if (f.type === FilterInputTypes.CHECKBOX) {
        const c = this.fieldValues[f.name];
        if (c === true) {
          qp[f.name] = true;
        }
      } else if (f.type === FilterInputTypes.NUMBER) {
        const n = this.fieldValues[f.name];
        if (n !== '' && n !== undefined && n !== null && !isNaN(Number(n))) {
          qp[f.name] = Number(n);
        }
      } else if (f.type === FilterInputTypes.BOOLEAN) {
        const b = this.fieldValues[f.name];
        if (b === true || b === false) {
          qp[f.name] = b;
        }
      } else {
        const v = this.fieldValues[f.name];
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          qp[f.name] = typeof v === 'number' ? v : String(v).trim();
        }
      }
    });

    if (this.orderByField) {
      if (this.orderByDescending) {
        qp['OrderByDescending'] = this.orderByField;
      } else {
        qp['OrderBy'] = this.orderByField;
      }
    }

    this.router.navigate([], {
      queryParams: qp,
      queryParamsHandling: 'merge',
    });
  }

  deleteFilters(): void {
    const qp: Record<string, null> = {};
    this.allowedFilterTypes.forEach((f) => {
      qp[f.name] = null;
    });
    qp['OrderBy'] = null;
    qp['OrderByDescending'] = null;

    this.fieldValues = {};
    this.dateValues = {};
    this.orderByField = '';
    this.orderByDescending = false;

    this.router.navigate([], {
      queryParams: qp,
      queryParamsHandling: 'merge',
    });
    this.removeFilters.emit();
  }

  trackFilter = (_: number, f: IFilterConfiguration) => f.name;

  /** Orden estable de grupos para el keyvalue pipe */
  returnZero = (): number => 0;

  get hasActiveFilters(): boolean {
    if (this.params['OrderBy'] || this.params['OrderByDescending']) {
      return true;
    }
    return this.allowedFilterTypes.some((f) => {
      const p = this.params[f.name];
      return p !== undefined && p !== null && String(p) !== '';
    });
  }
}
